// Cliente do carrinho nativo para a vitrine (2026-10-13).
//
// Módulo PURO (sem React, sem "server-only", sem fetch global): monta as
// requisições para /api/cart/native*, valida as respostas e decide quando
// recriar o carrinho. O transporte é injetado (`NativeCartSend`), então a
// lógica inteira roda sob o test runner do Node sem servidor. O provider
// components/Cart/NativeCartProvider.tsx só liga isto ao fetch e ao estado.
//
// Nunca fala com o WooCommerce: as únicas URLs montadas aqui são as rotas
// nativas abaixo.

import type {
  AddCartItemInput,
  Cart,
  CartAddress,
  CartItem,
  CartMoney,
  CheckoutShippingPackage,
  CheckoutShippingRate,
} from "../../types/cart.ts";
import type { NativeCartView, NativeCartViewItem } from "./nativeCartView.ts";
import type { ShippingCacheEntry } from "./shippingCalculator.ts";
import { getUnavailableNativeCartItemKeys, nativeCartToCart } from "./nativeCartToCart.ts";
import { parseNativeCartCouponView } from "../coupons/couponView.ts";

export const NATIVE_CART_ENDPOINT = "/api/cart/native";
export const NATIVE_CART_ITEMS_ENDPOINT = "/api/cart/native/items";
export const NATIVE_CART_SHIPPING_ESTIMATE_ENDPOINT = "/api/cart/native/shipping-estimate";

export const NATIVE_CART_LOAD_ERROR = "Não foi possível carregar o carrinho.";
export const NATIVE_CART_ADD_ERROR = "Não foi possível adicionar o produto. Tente novamente.";
export const NATIVE_CART_UPDATE_ERROR = "Não foi possível atualizar o item.";
export const NATIVE_CART_REMOVE_ERROR = "Não foi possível remover o item.";
export const NATIVE_CART_COUPONS_UNAVAILABLE = "Cupons ainda não estão disponíveis.";
export const NATIVE_CART_COUPON_ENDPOINT = "/api/cart/native/coupon";
export const NATIVE_CART_COUPON_ERROR = "Não foi possível atualizar o cupom agora.";
export const NATIVE_CART_SHIPPING_AT_CHECKOUT = "O frete é calculado no checkout.";
export const NATIVE_CART_SHIPPING_ERROR = "Não foi possível calcular o frete agora.";
export const NATIVE_CART_SHIPPING_SELECT_ERROR = "Não foi possível selecionar a entrega.";

export type NativeCartMethod = "GET" | "POST" | "PATCH" | "DELETE";

export interface NativeCartRequest {
  url: string;
  method: NativeCartMethod;
  body?: Record<string, unknown>;
}

export interface NativeCartHttpResult {
  status: number;
  ok: boolean;
  body: unknown;
}

export type NativeCartSend = (request: NativeCartRequest) => Promise<NativeCartHttpResult>;

export interface NativeCartFailure {
  kind: "error";
  status: number;
  code: string;
  message: string;
}

// `view: null` com kind "cart" = a mutação aconteceu, mas a visão falhou no
// servidor (nativeCartHandlers devolve `cart: null`): o chamador deve
// recarregar com GET em vez de tratar como erro.
export interface NativeCartSuccess {
  kind: "cart";
  cartId: string | null;
  view: NativeCartView | null;
}

export type NativeCartOutcome = NativeCartSuccess | NativeCartFailure;

// ---------- validação das respostas ----------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isNullableNumber(value: unknown): value is number | null {
  return value === null || (typeof value === "number" && Number.isFinite(value));
}

function isNativeCartViewItem(value: unknown): value is NativeCartViewItem {
  if (!isRecord(value)) return false;
  return (
    typeof value.productVariantId === "string" &&
    value.productVariantId.length > 0 &&
    isNullableNumber(value.wooProductId) &&
    isNullableNumber(value.wooParentProductId) &&
    typeof value.name === "string" &&
    isNullableString(value.variantLabel) &&
    isNullableString(value.sku) &&
    isNullableString(value.slug) &&
    isNullableString(value.imageUrl) &&
    typeof value.imageAlt === "string" &&
    isNullableString(value.unitPriceMinor) &&
    isNullableString(value.lineTotalMinor) &&
    typeof value.quantity === "number" &&
    Number.isInteger(value.quantity) &&
    value.quantity >= 0 &&
    isNullableNumber(value.maxQuantity) &&
    typeof value.freeShipping === "boolean" &&
    typeof value.available === "boolean"
  );
}

export function parseNativeCartView(value: unknown): NativeCartView | null {
  if (!isRecord(value)) return null;
  if (
    typeof value.cartId !== "string" ||
    typeof value.currency !== "string" ||
    typeof value.itemsCount !== "number" ||
    typeof value.itemsSubtotalMinor !== "string" ||
    !Array.isArray(value.items) ||
    !value.items.every(isNativeCartViewItem)
  ) {
    return null;
  }
  // Cupom nativo: opcional; malformado vira "sem cupom" (nunca derruba o carrinho).
  if (value.coupon !== undefined && value.coupon !== null) {
    return { ...(value as unknown as NativeCartView), coupon: parseNativeCartCouponView(value.coupon) };
  }
  return value as unknown as NativeCartView;
}

function readCartId(value: unknown): string | null {
  return isRecord(value) && typeof value.id === "string" && value.id.length > 0 ? value.id : null;
}

export function readNativeCartError(result: NativeCartHttpResult, fallback: string): NativeCartFailure {
  const body = isRecord(result.body) ? result.body : {};
  const message = typeof body.message === "string" && body.message.trim() ? body.message : fallback;
  const code = typeof body.code === "string" ? body.code : "UNKNOWN";
  return { kind: "error", status: result.status, code, message };
}

// GET /api/cart/native: `null` quando o convidado ainda não tem carrinho
// ativo; senão {id, ..., view}. view null = visão falhou no servidor.
export function interpretNativeCartSnapshot(result: NativeCartHttpResult): NativeCartOutcome {
  if (!result.ok) return readNativeCartError(result, NATIVE_CART_LOAD_ERROR);
  if (result.body === null) return { kind: "cart", cartId: null, view: emptyNativeCartView() };
  const cartId = readCartId(result.body);
  if (!cartId) return { kind: "error", status: result.status, code: "INVALID_RESPONSE", message: NATIVE_CART_LOAD_ERROR };
  const rawView = isRecord(result.body) ? result.body.view : null;
  if (rawView === null || rawView === undefined) {
    return { kind: "error", status: result.status, code: "CART_VIEW_UNAVAILABLE", message: NATIVE_CART_LOAD_ERROR };
  }
  const view = parseNativeCartView(rawView);
  if (!view) return { kind: "error", status: result.status, code: "INVALID_RESPONSE", message: NATIVE_CART_LOAD_ERROR };
  return { kind: "cart", cartId, view };
}

// Respostas das rotas de item: `cart` traz a visão completa (ou null quando
// a visão falhou depois da mutação já feita).
export function interpretNativeCartMutation(
  result: NativeCartHttpResult,
  cartId: string,
  fallback: string,
): NativeCartOutcome {
  if (!result.ok) return readNativeCartError(result, fallback);
  const rawView = isRecord(result.body) ? result.body.cart : null;
  if (rawView === null || rawView === undefined) return { kind: "cart", cartId, view: null };
  const view = parseNativeCartView(rawView);
  // Visão malformada depois de uma mutação bem-sucedida: mesmo tratamento de
  // `cart: null` (recarregar), nunca um erro que convide a repetir a ação.
  return { kind: "cart", cartId, view };
}

// ---------- montagem das requisições ----------

export function resolveNativeWooProductId(input: AddCartItemInput): number {
  return input.variationId ?? input.productId;
}

export function buildGetNativeCartRequest(): NativeCartRequest {
  return { url: NATIVE_CART_ENDPOINT, method: "GET" };
}

export function buildCreateNativeCartRequest(): NativeCartRequest {
  return { url: NATIVE_CART_ENDPOINT, method: "POST" };
}

export function buildAddNativeCartItemRequest(
  cartId: string,
  input: AddCartItemInput,
  idempotencyKey: string,
): NativeCartRequest {
  return {
    url: NATIVE_CART_ITEMS_ENDPOINT,
    method: "POST",
    body: {
      cartId,
      wooProductId: resolveNativeWooProductId(input),
      quantity: input.quantity ?? 1,
      idempotencyKey,
    },
  };
}

export function nativeCartItemUrl(key: string): string {
  return `${NATIVE_CART_ITEMS_ENDPOINT}/${encodeURIComponent(key)}`;
}

export function buildUpdateNativeCartItemRequest(
  cartId: string,
  key: string,
  quantity: number,
  idempotencyKey: string,
): NativeCartRequest {
  return { url: nativeCartItemUrl(key), method: "PATCH", body: { cartId, quantity, idempotencyKey } };
}

export function buildRemoveNativeCartItemRequest(cartId: string, key: string, idempotencyKey: string): NativeCartRequest {
  return { url: nativeCartItemUrl(key), method: "DELETE", body: { cartId, idempotencyKey } };
}

// Carrinho do cookie que não aceita mais itens (convertido depois de um
// pedido, expirado) ou que sumiu: criar um novo e tentar de novo, uma vez.
export function shouldRecreateNativeCart(failure: NativeCartFailure): boolean {
  return (
    (failure.status === 409 && failure.code === "CART_NOT_MUTABLE") ||
    (failure.status === 404 && failure.code === "CART_NOT_FOUND")
  );
}

// ---------- orquestração ----------

export async function createNativeCart(send: NativeCartSend): Promise<NativeCartOutcome> {
  const result = await send(buildCreateNativeCartRequest());
  if (!result.ok) return readNativeCartError(result, NATIVE_CART_ADD_ERROR);
  const cartId = readCartId(result.body);
  if (!cartId) return { kind: "error", status: result.status, code: "INVALID_RESPONSE", message: NATIVE_CART_ADD_ERROR };
  // POST /api/cart/native não devolve a visão; quem chama segue para a
  // adição do item, cuja resposta traz o carrinho completo.
  return { kind: "cart", cartId, view: null };
}

export interface AddNativeCartItemOptions {
  send: NativeCartSend;
  // Carrinho ativo conhecido (ou null para criar um).
  cartId: string | null;
  input: AddCartItemInput;
  newIdempotencyKey: () => string;
}

export async function addItemToNativeCart({
  send,
  cartId,
  input,
  newIdempotencyKey,
}: AddNativeCartItemOptions): Promise<NativeCartOutcome> {
  let activeCartId = cartId;
  let recreated = false;

  if (!activeCartId) {
    const created = await createNativeCart(send);
    if (created.kind === "error") return created;
    activeCartId = created.cartId;
    recreated = true;
  }

  for (;;) {
    if (!activeCartId) {
      return { kind: "error", status: 502, code: "INVALID_RESPONSE", message: NATIVE_CART_ADD_ERROR };
    }
    const result = await send(buildAddNativeCartItemRequest(activeCartId, input, newIdempotencyKey()));
    const outcome = interpretNativeCartMutation(result, activeCartId, NATIVE_CART_ADD_ERROR);
    if (outcome.kind === "cart" || recreated || !shouldRecreateNativeCart(outcome)) return outcome;

    const created = await createNativeCart(send);
    if (created.kind === "error") return created;
    activeCartId = created.cartId;
    recreated = true;
  }
}

// ---------- mapeamento para a vitrine ----------

export function emptyNativeCartView(currency = "BRL"): NativeCartView {
  return { cartId: "", currency, itemsCount: 0, itemsSubtotalMinor: "0", items: [] };
}

export interface MappedNativeCart {
  cart: Cart;
  unavailableItemKeys: string[];
}

export function mapNativeCartView(view: NativeCartView): MappedNativeCart {
  return { cart: nativeCartToCart(view), unavailableItemKeys: getUnavailableNativeCartItemKeys(view) };
}

// Item recém-adicionado, para o evento add_to_cart. Compara pelo id Woo que
// foi enviado (variação ou produto simples), que é o que o mapper expõe em
// variationId (quando difere do pai) ou productId.
export function findNativeCartItemByWooProductId(cart: Cart, wooProductId: number): CartItem | undefined {
  return cart.items.find((item) => (item.variationId ?? item.productId) === wooProductId);
}

// ---------- estimativa de frete no carrinho ----------
//
// POST /api/cart/native/shipping-estimate devolve as opções no MESMO formato
// de tarifa da vitrine (CheckoutShippingPackage), calculadas pelo servidor
// com as regras do checkout nativo. A escolha no carrinho é só local (uma
// estimativa exibida no resumo): a seleção que vale -- e o valor cobrado --
// acontece no checkout, que cota de novo a partir do carrinho congelado.

export interface NativeCartShippingEstimate {
  // getCartShippingContextKey do carrinho para o qual a estimativa vale;
  // mudou o carrinho, a estimativa deixa de valer.
  contextKey: string;
  postcode: string;
  destination?: CartAddress;
  packages: CheckoutShippingPackage[];
}

export interface NativeCartShippingEstimateSuccess {
  kind: "estimate";
  postcode: string;
  destination?: CartAddress;
  packages: CheckoutShippingPackage[];
}

export function buildNativeCartShippingEstimateRequest(postcode: string): NativeCartRequest {
  return { url: NATIVE_CART_SHIPPING_ESTIMATE_ENDPOINT, method: "POST", body: { postcode: postcode.replace(/\D/g, "") } };
}

function isMoney(value: unknown): value is CartMoney {
  return isRecord(value) && typeof value.value === "string" && /^\d+$/.test(value.value) && typeof value.currencyCode === "string";
}

function isShippingRate(value: unknown): value is CheckoutShippingRate {
  return (
    isRecord(value) &&
    typeof value.rateId === "string" &&
    value.rateId.length > 0 &&
    typeof value.name === "string" &&
    (typeof value.packageId === "string" || typeof value.packageId === "number") &&
    typeof value.selected === "boolean" &&
    isMoney(value.price)
  );
}

function isShippingPackage(value: unknown): value is CheckoutShippingPackage {
  return (
    isRecord(value) &&
    (typeof value.packageId === "string" || typeof value.packageId === "number") &&
    Array.isArray(value.rates) &&
    value.rates.every(isShippingRate)
  );
}

function readDestination(value: unknown): CartAddress | undefined {
  if (!isRecord(value)) return undefined;
  const address: CartAddress = {};
  for (const key of ["address1", "address2", "city", "state", "postcode", "country"] as const) {
    if (typeof value[key] === "string") address[key] = value[key] as string;
  }
  return address;
}

export function interpretNativeCartShippingEstimate(
  result: NativeCartHttpResult,
): NativeCartShippingEstimateSuccess | NativeCartFailure {
  if (!result.ok) return readNativeCartError(result, NATIVE_CART_SHIPPING_ERROR);
  const body = isRecord(result.body) ? result.body : null;
  if (
    !body ||
    typeof body.destinationPostcode !== "string" ||
    !/^\d{8}$/.test(body.destinationPostcode) ||
    !Array.isArray(body.shippingPackages) ||
    !body.shippingPackages.every(isShippingPackage)
  ) {
    return { kind: "error", status: result.status, code: "INVALID_RESPONSE", message: NATIVE_CART_SHIPPING_ERROR };
  }
  return {
    kind: "estimate",
    postcode: body.destinationPostcode,
    destination: readDestination(body.destination),
    packages: body.shippingPackages,
  };
}

function selectedRateOf(packages: readonly CheckoutShippingPackage[]): CheckoutShippingRate | undefined {
  for (const shippingPackage of packages) {
    const rate = shippingPackage.rates.find((item) => item.selected);
    if (rate) return rate;
  }
  return undefined;
}

function withSelection(
  packages: readonly CheckoutShippingPackage[],
  packageId: number | string,
  rateId: string | null,
): CheckoutShippingPackage[] {
  return packages.map((shippingPackage) => ({
    ...shippingPackage,
    rates: shippingPackage.rates.map((rate) => ({
      ...rate,
      selected: rateId !== null && shippingPackage.packageId === packageId && rate.rateId === rateId,
    })),
  }));
}

/** Escolha local de uma opção da estimativa; null quando a opção não existe. */
export function selectNativeShippingEstimateRate(
  estimate: NativeCartShippingEstimate | null,
  packageId: number | string,
  rateId: string,
): NativeCartShippingEstimate | null {
  if (!estimate) return null;
  const exists = estimate.packages.some(
    (shippingPackage) => shippingPackage.packageId === packageId && shippingPackage.rates.some((rate) => rate.rateId === rateId),
  );
  if (!exists) return null;
  return { ...estimate, packages: withSelection(estimate.packages, packageId, rateId) };
}

/**
 * Estimativa nova a partir da resposta do servidor. Mesmo CEP e a opção
 * escolhida antes ainda existe -> continua escolhida (como o carrinho do
 * Woo mantinha o método escolhido ao recalcular).
 */
export function buildNativeShippingEstimate(
  response: NativeCartShippingEstimateSuccess,
  contextKey: string,
  previous: NativeCartShippingEstimate | null,
): NativeCartShippingEstimate {
  const base: NativeCartShippingEstimate = {
    contextKey,
    postcode: response.postcode,
    destination: response.destination,
    packages: withSelection(response.packages, "", null),
  };
  const previousRate = previous && previous.postcode === response.postcode ? selectedRateOf(previous.packages) : undefined;
  if (!previousRate) return base;
  return selectNativeShippingEstimateRate(base, previousRate.packageId, previousRate.rateId) ?? base;
}

/**
 * Estimativa reconstruída do cache da calculadora (localStorage), para o
 * resumo do carrinho mostrar o frete já escolhido depois de recarregar a
 * página -- a calculadora restaura a escolha do cache sem chamar o servidor.
 */
export function nativeShippingEstimateFromCache(entry: ShippingCacheEntry | null, contextKey: string): NativeCartShippingEstimate | null {
  if (!entry || entry.contextKey !== contextKey || !Array.isArray(entry.quote?.packages)) return null;
  if (!entry.quote.packages.every(isShippingPackage)) return null;
  const packages = entry.selected
    ? withSelection(entry.quote.packages, entry.selected.packageId, entry.selected.rateId)
    : withSelection(entry.quote.packages, "", null);
  return { contextKey, postcode: entry.postcode, destination: entry.quote.destination, packages };
}

/**
 * Carrinho com a estimativa aplicada: tarifas para a calculadora e, quando
 * há opção escolhida, frete e total no resumo (senão "A calcular"). Função
 * idempotente: o total é sempre itens + frete escolhido.
 */
export function applyNativeShippingEstimate(cart: Cart, estimate: NativeCartShippingEstimate | null): Cart {
  const items = cart.totals.items;
  const itemsMinor = /^\d+$/.test(items.value) ? BigInt(items.value) : BigInt(0);
  const selected = estimate ? selectedRateOf(estimate.packages) : undefined;
  const shippingMinor = selected && /^\d+$/.test(selected.price.value) ? BigInt(selected.price.value) : BigInt(0);
  const money = (value: bigint): CartMoney => ({ ...items, value: value.toString() });
  // Desconto do cupom nativo nos produtos (zero sem cupom): o total mostrado
  // continua sendo produtos - desconto + frete estimado.
  const discountMinor = /^\d+$/.test(cart.totals.discount.value) ? BigInt(cart.totals.discount.value) : BigInt(0);
  return {
    ...cart,
    shippingPackages: estimate?.packages ?? [],
    shippingDestination: estimate?.destination,
    hasCalculatedShipping: Boolean(selected),
    totals: {
      ...cart.totals,
      shipping: money(shippingMinor),
      shippingTax: money(BigInt(0)),
      price: money(itemsMinor - discountMinor + shippingMinor),
    },
  };
}

// ---------- cupom nativo (NATIVE_COUPONS=on) ----------
//
// POST aplica (substitui o cupom anterior), DELETE remove, GET relê o
// carrinho com o cupom recalculado (funciona também com o carrinho travado
// pelo checkout). As três respondem {id, view}, igual ao GET do carrinho.

export function buildApplyNativeCouponRequest(code: string): NativeCartRequest {
  return { url: NATIVE_CART_COUPON_ENDPOINT, method: "POST", body: { code: code.trim() } };
}

export function buildRemoveNativeCouponRequest(): NativeCartRequest {
  return { url: NATIVE_CART_COUPON_ENDPOINT, method: "DELETE" };
}

export function buildGetNativeCouponRequest(): NativeCartRequest {
  return { url: NATIVE_CART_COUPON_ENDPOINT, method: "GET" };
}

export function interpretNativeCouponResult(result: NativeCartHttpResult): NativeCartOutcome {
  if (!result.ok) return readNativeCartError(result, NATIVE_CART_COUPON_ERROR);
  if (result.body === null) return { kind: "error", status: result.status, code: "INVALID_RESPONSE", message: NATIVE_CART_COUPON_ERROR };
  const outcome = interpretNativeCartSnapshot(result);
  return outcome.kind === "error" ? { ...outcome, message: NATIVE_CART_COUPON_ERROR } : outcome;
}

/** Mensagem para o cliente depois de aplicar: o cupom pode entrar no carrinho sem valer agora. */
export function nativeCouponAppliedMessage(view: NativeCartView | null): string {
  const coupon = view?.coupon;
  if (!coupon) return "Cupom aplicado.";
  if (!coupon.applied) return coupon.message ?? "O cupom foi adicionado, mas não gera desconto agora.";
  return coupon.freeShipping ? "Cupom de frete grátis aplicado. O desconto aparece ao escolher a entrega." : "Cupom aplicado.";
}
